import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MousePointer2, Square, PenLine, X, Check } from 'lucide-react';
import * as THREE from 'three';
import { pickWorldPointFromPointer } from '../utils/viewerSpatialUtils';
import type { ActiveContextSelection } from '../utils/viewerSpatialUtils';
import type { WorldPos } from '../utils/measureUtils';
import { projectToScreen } from '../utils/measureUtils';

type SelectionMode = 'click' | 'box' | 'lasso';
type DrawState = 'idle' | 'drawing' | 'done';

export interface SelectionToolProps {
  viewerRef: React.MutableRefObject<any>;
  isActive: boolean;
  onClose: () => void;
  onSelectionChange: (data: ActiveContextSelection | null) => void;
  onDone?: (selection: ActiveContextSelection) => void;
  initialMode?: SelectionMode;
  previewTone?: 'cyan' | 'red';
}

const GRID_N = 6;
const MAX_SAMPLE_POINTS = 50;
const MIN_HIT_COUNT = 3;
const LASSO_THROTTLE_PX = 8;
const BOUNDS_TRIM_FRACTION = 0.1;
const DEPTH_CLUSTER_GAP_METERS = 1.25;
const DEPTH_CLUSTER_GAP_FACTOR = 0.025;

function pointInPolygon(px: number, py: number, pts: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const { x: xi, y: yi } = pts[i];
    const { x: xj, y: yj } = pts[j];
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

function dist3(a: WorldPos, b: { x: number; y: number; z: number }): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2);
}

function subsample<T>(arr: T[], maxN: number): T[] {
  if (arr.length <= maxN) return arr;
  const step = arr.length / maxN;
  return Array.from({ length: maxN }, (_, i) => arr[Math.floor(i * step)]);
}

function applyDepthFilter(hits: WorldPos[], camPos: { x: number; y: number; z: number }): WorldPos[] {
  if (hits.length < 4) return hits;
  const entries = hits
    .map((hit) => ({ hit, dist: dist3(hit, camPos) }))
    .sort((a, b) => a.dist - b.dist);
  const median = entries[Math.floor(entries.length / 2)].dist;
  const gapThreshold = Math.max(DEPTH_CLUSTER_GAP_METERS, median * DEPTH_CLUSTER_GAP_FACTOR);
  const clusters: typeof entries[] = [];
  let current: typeof entries = [entries[0]];

  for (let i = 1; i < entries.length; i++) {
    if (entries[i].dist - entries[i - 1].dist > gapThreshold) {
      clusters.push(current);
      current = [];
    }
    current.push(entries[i]);
  }
  clusters.push(current);

  const largest = clusters.reduce((best, cluster) => {
    if (cluster.length > best.length) return cluster;
    if (cluster.length < best.length) return best;
    const clusterMean = cluster.reduce((sum, item) => sum + item.dist, 0) / cluster.length;
    const bestMean = best.reduce((sum, item) => sum + item.dist, 0) / best.length;
    return Math.abs(clusterMean - median) < Math.abs(bestMean - median) ? cluster : best;
  }, clusters[0]);

  return largest.map(item => item.hit);
}

function trimmedRange(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 0 };
  if (values.length < 8) return { min: Math.min(...values), max: Math.max(...values) };

  const sorted = [...values].sort((a, b) => a - b);
  const trim = Math.min(
    Math.floor(sorted.length * BOUNDS_TRIM_FRACTION),
    Math.floor((sorted.length - 2) / 2),
  );
  return {
    min: sorted[trim],
    max: sorted[sorted.length - 1 - trim],
  };
}

function buildWorldBounds(hits: WorldPos[]): ActiveContextSelection['worldBounds'] {
  const x = trimmedRange(hits.map(h => h.x));
  const y = trimmedRange(hits.map(h => h.y));
  const z = trimmedRange(hits.map(h => h.z));

  return {
    min: { x: x.min, y: y.min, z: z.min },
    max: { x: x.max, y: y.max, z: z.max },
  };
}

function buildOrientedBounds(
  hits: WorldPos[],
  camera: THREE.Camera,
): ActiveContextSelection['orientedBounds'] {
  if (hits.length === 0) return undefined;

  camera.updateMatrixWorld(true);
  camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

  const localPoints = hits.map((hit) => (
    new THREE.Vector3(hit.x, hit.y, hit.z).applyMatrix4(camera.matrixWorldInverse)
  ));
  const x = trimmedRange(localPoints.map(p => p.x));
  const y = trimmedRange(localPoints.map(p => p.y));
  const z = trimmedRange(localPoints.map(p => p.z));
  const centerLocal = new THREE.Vector3(
    (x.min + x.max) / 2,
    (y.min + y.max) / 2,
    (z.min + z.max) / 2,
  );
  const centerWorld = centerLocal.clone().applyMatrix4(camera.matrixWorld);
  const quaternion = new THREE.Quaternion();
  camera.getWorldQuaternion(quaternion);

  return {
    center: { x: centerWorld.x, y: centerWorld.y, z: centerWorld.z },
    size: {
      x: Math.max(x.max - x.min, 0.001),
      y: Math.max(y.max - y.min, 0.001),
      z: Math.max(z.max - z.min, 0.001),
    },
    quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
  };
}

function buildSelection(
  mode: SelectionMode,
  rawHits: WorldPos[],
  attemptedSamples: number,
  camera: THREE.Camera & { position: THREE.Vector3 },
  screenShape: ActiveContextSelection['screenShape'],
): ActiveContextSelection | null {
  if (rawHits.length === 0) return null;

  const camPos = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
  let hits: WorldPos[];
  let usedFallback = false;

  if (mode === 'click') {
    hits = rawHits;
  } else {
    const filtered = applyDepthFilter(rawHits, camPos);
    if (filtered.length >= MIN_HIT_COUNT) {
      hits = filtered;
    } else {
      hits = rawHits;
      usedFallback = true;
    }
  }

  const cx = hits.reduce((s, h) => s + h.x, 0) / hits.length;
  const cy = hits.reduce((s, h) => s + h.y, 0) / hits.length;
  const cz = hits.reduce((s, h) => s + h.z, 0) / hits.length;

  const hitRate = hits.length / Math.max(attemptedSamples, 1);
  const confidence: 'high' | 'medium' | 'low' = usedFallback
    ? 'low'
    : hitRate >= 0.3 ? 'high' : hitRate >= 0.1 ? 'medium' : 'low';

  const worldBounds = buildWorldBounds(hits);
  const orientedBounds = mode === 'click' ? undefined : buildOrientedBounds(hits, camera);

  return {
    mode,
    worldCenter: { x: cx, y: cy, z: cz },
    worldBounds,
    orientedBounds,
    samplePoints: subsample(hits, MAX_SAMPLE_POINTS),
    hitCount: hits.length,
    attemptedSamples,
    confidence,
    screenShape,
  };
}

const CONFIDENCE_COLOR = { high: '#4ade80', medium: '#fbbf24', low: '#f87171' } as const;
export default function SelectionTool({ viewerRef, isActive, onClose, onSelectionChange, onDone, initialMode, previewTone = 'cyan' }: SelectionToolProps) {
  const shapeStroke = previewTone === 'red' ? 'rgba(239,68,68,0.9)' : 'rgba(99,210,255,0.75)';
  const shapeFill = previewTone === 'red' ? 'rgba(239,68,68,0.07)' : 'rgba(99,210,255,0.07)';
  const [mode, setMode] = useState<SelectionMode>(initialMode ?? 'box');
  const [drawState, setDrawState] = useState<DrawState>('idle');
  const [boxStart, setBoxStart] = useState<{ x: number; y: number } | null>(null);
  const [boxCurrent, setBoxCurrent] = useState<{ x: number; y: number } | null>(null);
  const [lassoPath, setLassoPath] = useState<{ x: number; y: number }[]>([]);
  const [selection, setSelection] = useState<ActiveContextSelection | null>(null);
  // Pixel coords of the drawn shape for SVG rendering (not normalized)
  const [drawnShape, setDrawnShape] = useState<{ kind: SelectionMode; pts: { x: number; y: number }[] } | null>(null);
  const [centerScreen, setCenterScreen] = useState<{ x: number; y: number; visible: boolean } | null>(null);

  const modeRef = useRef<SelectionMode>(initialMode ?? 'box');
  const drawStateRef = useRef<DrawState>('idle');
  const boxStartRef = useRef<{ x: number; y: number } | null>(null);
  const lassoPathRef = useRef<{ x: number; y: number }[]>([]);
  const selectionRef = useRef<ActiveContextSelection | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const rafRef = useRef<number>(0);

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { drawStateRef.current = drawState; }, [drawState]);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; }, [onSelectionChange]);

  // RAF: project worldCenter to screen coords for the crosshair
  useEffect(() => {
    const tick = () => {
      const sel = selectionRef.current;
      const viewer = viewerRef.current;
      const camera = viewer?.camera;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      if (sel && camera && canvas) {
        setCenterScreen(projectToScreen(sel.worldCenter, camera, canvas));
      } else {
        setCenterScreen(null);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewerRef]);

  // 3D bounding box preview — rendered directly in viewer.threeScene
  useEffect(() => {
    if (!selection || selection.mode === 'click' || !isActive) return;
    const threeScene = (viewerRef.current as any)?.threeScene as THREE.Scene | undefined;
    if (!threeScene) return;

    const oriented = selection.orientedBounds;
    const { min, max } = selection.worldBounds;
    const sx = oriented?.size.x ?? (max.x - min.x);
    const sy = oriented?.size.y ?? (max.y - min.y);
    const sz = oriented?.size.z ?? (max.z - min.z);
    // Skip degenerate boxes (all axes near-zero)
    if (sx < 0.001 && sy < 0.001 && sz < 0.001) return;

    const center = oriented
      ? new THREE.Vector3(oriented.center.x, oriented.center.y, oriented.center.z)
      : new THREE.Vector3(
        (min.x + max.x) / 2,
        (min.y + max.y) / 2,
        (min.z + max.z) / 2,
      );
    const quaternion = oriented
      ? new THREE.Quaternion(...oriented.quaternion)
      : new THREE.Quaternion();
    const boxGeo = new THREE.BoxGeometry(
      Math.max(sx, 0.001),
      Math.max(sy, 0.001),
      Math.max(sz, 0.001),
    );
    const edgesGeo = new THREE.EdgesGeometry(boxGeo);

    const fillMat = new THREE.MeshBasicMaterial({
      color: previewTone === 'red' ? 0xef4444 : 0x63d2ff,
      transparent: true,
      opacity: 0.07,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const edgeMat = new THREE.LineBasicMaterial({
      color: previewTone === 'red' ? 0xef4444 : 0x63d2ff,
      transparent: true,
      opacity: 0.75,
    });

    const mesh = new THREE.Mesh(boxGeo, fillMat);
    mesh.position.copy(center);
    mesh.quaternion.copy(quaternion);
    const edges = new THREE.LineSegments(edgesGeo, edgeMat);
    edges.position.copy(center);
    edges.quaternion.copy(quaternion);

    threeScene.add(mesh);
    threeScene.add(edges);

    return () => {
      threeScene.remove(mesh);
      threeScene.remove(edges);
      boxGeo.dispose();
      edgesGeo.dispose();
      fillMat.dispose();
      edgeMat.dispose();
    };
  }, [selection, viewerRef, isActive, previewTone]);

  const clearSelection = useCallback(() => {
    setSelection(null);
    selectionRef.current = null;
    setDrawState('idle');
    drawStateRef.current = 'idle';
    setBoxStart(null);
    boxStartRef.current = null;
    setBoxCurrent(null);
    setLassoPath([]);
    lassoPathRef.current = [];
    setDrawnShape(null);
    onSelectionChangeRef.current(null);
  }, []);

  useEffect(() => {
    if (!isActive) {
      setDrawState('idle');
      drawStateRef.current = 'idle';
      setBoxStart(null);
      boxStartRef.current = null;
      setBoxCurrent(null);
      setLassoPath([]);
      lassoPathRef.current = [];
    }
  }, [isActive]);

  // Pointer event handling — all capture:true to prevent viewer from seeing events
  useEffect(() => {
    const canvas = viewerRef.current?.renderer?.domElement as HTMLCanvasElement | undefined;
    if (!canvas || !isActive) return;

    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';

    const blockViewer = (viewer: any) => {
      if (!viewer) return;
      viewer.clickTransitioning = false;
      viewer.transitioningCameraTarget = false;
      if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
      if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
    };

    const finishSelection = (rawHits: WorldPos[], attempted: number, shape: ActiveContextSelection['screenShape'], shapePts: { x: number; y: number }[], shapeKind: SelectionMode) => {
      const viewer = viewerRef.current;
      const camera = viewer?.camera;
      if (!camera) return;
      const result = buildSelection(modeRef.current, rawHits, attempted, camera, shape);
      setSelection(result);
      selectionRef.current = result;
      setDrawState('done');
      drawStateRef.current = 'done';
      setDrawnShape({ kind: shapeKind, pts: shapePts });
      onSelectionChangeRef.current(result);
    };

    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      blockViewer(viewerRef.current);

      const viewer = viewerRef.current;
      const rect = canvas.getBoundingClientRect();

      if (modeRef.current === 'click') {
        const hit = pickWorldPointFromPointer(viewer, e.clientX, e.clientY);
        const nx = (e.clientX - rect.left) / rect.width;
        const ny = (e.clientY - rect.top) / rect.height;
        finishSelection(
          hit ? [hit] : [],
          1,
          { kind: 'click', points: [{ x: nx, y: ny }] },
          [{ x: e.clientX, y: e.clientY }],
          'click',
        );
        return;
      }

      if (modeRef.current === 'box') {
        canvas.setPointerCapture(e.pointerId);
        const pos = { x: e.clientX, y: e.clientY };
        setBoxStart(pos);
        boxStartRef.current = pos;
        setBoxCurrent(pos);
        setDrawState('drawing');
        drawStateRef.current = 'drawing';
        return;
      }

      if (modeRef.current === 'lasso') {
        canvas.setPointerCapture(e.pointerId);
        const pos = [{ x: e.clientX, y: e.clientY }];
        lassoPathRef.current = pos;
        setLassoPath(pos);
        setDrawState('drawing');
        drawStateRef.current = 'drawing';
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (drawStateRef.current !== 'drawing') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      blockViewer(viewerRef.current);

      if (modeRef.current === 'box') {
        setBoxCurrent({ x: e.clientX, y: e.clientY });
        return;
      }

      if (modeRef.current === 'lasso') {
        const path = lassoPathRef.current;
        const last = path[path.length - 1];
        const dx = e.clientX - last.x;
        const dy = e.clientY - last.y;
        if (dx * dx + dy * dy >= LASSO_THROTTLE_PX * LASSO_THROTTLE_PX) {
          const next = [...path, { x: e.clientX, y: e.clientY }];
          lassoPathRef.current = next;
          setLassoPath(next);
        }
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (drawStateRef.current !== 'drawing') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      blockViewer(viewerRef.current);

      const viewer = viewerRef.current;
      const rect = canvas.getBoundingClientRect();

      if (modeRef.current === 'box') {
        const start = boxStartRef.current;
        if (!start) return;

        const minCX = Math.min(start.x, e.clientX);
        const maxCX = Math.max(start.x, e.clientX);
        const minCY = Math.min(start.y, e.clientY);
        const maxCY = Math.max(start.y, e.clientY);

        // Tiny drag → treat as click
        if (maxCX - minCX < 10 && maxCY - minCY < 10) {
          const hit = pickWorldPointFromPointer(viewer, (minCX + maxCX) / 2, (minCY + maxCY) / 2);
          const nx = ((minCX + maxCX) / 2 - rect.left) / rect.width;
          const ny = ((minCY + maxCY) / 2 - rect.top) / rect.height;
          finishSelection(
            hit ? [hit] : [], 1,
            { kind: 'click', points: [{ x: nx, y: ny }] },
            [{ x: (minCX + maxCX) / 2, y: (minCY + maxCY) / 2 }],
            'click',
          );
          return;
        }

        const hits: WorldPos[] = [];
        let attempted = 0;
        for (let ix = 0; ix < GRID_N; ix++) {
          for (let iy = 0; iy < GRID_N; iy++) {
            const cx = minCX + (ix / (GRID_N - 1)) * (maxCX - minCX);
            const cy = minCY + (iy / (GRID_N - 1)) * (maxCY - minCY);
            attempted++;
            const hit = pickWorldPointFromPointer(viewer, cx, cy);
            if (hit) hits.push(hit);
          }
        }

        const corners = [
          { x: (minCX - rect.left) / rect.width, y: (minCY - rect.top) / rect.height },
          { x: (maxCX - rect.left) / rect.width, y: (minCY - rect.top) / rect.height },
          { x: (maxCX - rect.left) / rect.width, y: (maxCY - rect.top) / rect.height },
          { x: (minCX - rect.left) / rect.width, y: (maxCY - rect.top) / rect.height },
        ];
        finishSelection(hits, attempted, { kind: 'box', points: corners },
          [{ x: minCX, y: minCY }, { x: maxCX, y: minCY }, { x: maxCX, y: maxCY }, { x: minCX, y: maxCY }],
          'box',
        );
        setBoxCurrent({ x: e.clientX, y: e.clientY });
        return;
      }

      if (modeRef.current === 'lasso') {
        const path = lassoPathRef.current;
        if (path.length < 3) { clearSelection(); return; }

        const xs = path.map(p => p.x);
        const ys = path.map(p => p.y);
        const minCX = Math.min(...xs), maxCX = Math.max(...xs);
        const minCY = Math.min(...ys), maxCY = Math.max(...ys);

        const hits: WorldPos[] = [];
        let attempted = 0;
        for (let ix = 0; ix < GRID_N; ix++) {
          for (let iy = 0; iy < GRID_N; iy++) {
            const cx = minCX + (ix / (GRID_N - 1)) * (maxCX - minCX);
            const cy = minCY + (iy / (GRID_N - 1)) * (maxCY - minCY);
            if (!pointInPolygon(cx, cy, path)) continue;
            attempted++;
            const hit = pickWorldPointFromPointer(viewer, cx, cy);
            if (hit) hits.push(hit);
          }
        }

        const rect2 = canvas.getBoundingClientRect();
        const normPath = path.map(p => ({ x: (p.x - rect2.left) / rect2.width, y: (p.y - rect2.top) / rect2.height }));
        finishSelection(hits, attempted, { kind: 'lasso', points: normPath }, path, 'lasso');
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown, true);
    canvas.addEventListener('pointermove', onPointerMove, true);
    canvas.addEventListener('pointerup', onPointerUp, true);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown, true);
      canvas.removeEventListener('pointermove', onPointerMove, true);
      canvas.removeEventListener('pointerup', onPointerUp, true);
      canvas.style.cursor = prevCursor;
    };
  }, [viewerRef, isActive, clearSelection]);

  // ── SVG helpers ──────────────────────────────────────────────────────────

  const liveBoxRect = (() => {
    if (drawState !== 'drawing' || mode !== 'box' || !boxStart || !boxCurrent) return null;
    return {
      x: Math.min(boxStart.x, boxCurrent.x),
      y: Math.min(boxStart.y, boxCurrent.y),
      w: Math.abs(boxCurrent.x - boxStart.x),
      h: Math.abs(boxCurrent.y - boxStart.y),
    };
  })();

  const lassoD = (() => {
    const pts = drawState === 'drawing' ? lassoPath : drawnShape?.kind === 'lasso' ? drawnShape.pts : null;
    if (!pts || pts.length < 2) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';
  })();

  const frozenBoxRect = (() => {
    if (drawState !== 'done' || drawnShape?.kind !== 'box') return null;
    const pts = drawnShape.pts;
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  })();

  const confidenceColor = selection ? CONFIDENCE_COLOR[selection.confidence] : '#fff';
  const statusText = (() => {
    if (drawState === 'idle') {
      if (mode === 'click') return 'Click to select a point';
      if (mode === 'box') return 'Drag to draw a box';
      return 'Draw around an object';
    }
    if (drawState === 'drawing') return 'Drawing…';
    if (!selection || selection.hitCount === 0) return 'No geometry hit — try again';
    return `${selection.hitCount}/${selection.attemptedSamples} hits · ${selection.confidence} confidence`;
  })();

  if (!isActive) return null;

  return (
    <>
      {/* SVG overlay — full-screen, pointer-events: none */}
      <svg
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1001 }}
      >
        {/* Live box */}
        {liveBoxRect && (
          <rect
            x={liveBoxRect.x} y={liveBoxRect.y} width={liveBoxRect.w} height={liveBoxRect.h}
            fill={shapeFill} stroke={shapeStroke} strokeWidth={1.5} strokeDasharray="6 3"
          />
        )}

        {/* Live lasso */}
        {drawState === 'drawing' && mode === 'lasso' && lassoD && (
          <path d={lassoD} fill={shapeFill} stroke={shapeStroke} strokeWidth={1.5} strokeDasharray="6 3" />
        )}

        {/* Frozen box */}
        {frozenBoxRect && (
          <rect
            x={frozenBoxRect.x} y={frozenBoxRect.y} width={frozenBoxRect.w} height={frozenBoxRect.h}
            fill={shapeFill} stroke={previewTone === 'red' ? shapeStroke : confidenceColor} strokeWidth={1.5} strokeDasharray="4 2" opacity={0.8}
          />
        )}

        {/* Frozen lasso */}
        {drawState === 'done' && drawnShape?.kind === 'lasso' && lassoD && (
          <path d={lassoD} fill={shapeFill} stroke={previewTone === 'red' ? shapeStroke : confidenceColor} strokeWidth={1.5} strokeDasharray="4 2" opacity={0.8} />
        )}

        {/* Center crosshair */}
        {drawState === 'done' && centerScreen?.visible && (
          <g transform={`translate(${centerScreen.x},${centerScreen.y})`}>
            <circle r={6} fill="none" stroke={confidenceColor} strokeWidth={1.5} />
            <line x1={-10} y1={0} x2={10} y2={0} stroke={confidenceColor} strokeWidth={1} />
            <line x1={0} y1={-10} x2={0} y2={10} stroke={confidenceColor} strokeWidth={1} />
          </g>
        )}

        {/* Click indicator */}
        {drawState === 'done' && drawnShape?.kind === 'click' && drawnShape.pts[0] && (
          <g transform={`translate(${drawnShape.pts[0].x},${drawnShape.pts[0].y})`}>
            <circle r={10} fill="none" stroke={confidenceColor} strokeWidth={1.5} />
            <circle r={3} fill={confidenceColor} />
          </g>
        )}
      </svg>

      {/* Control panel */}
      <div
        className="fixed z-[1002] w-56 rounded-2xl overflow-hidden"
        style={{
          top: 68, right: 16,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10">
          <span style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>Select Context</span>
          <button
            onClick={() => { clearSelection(); onClose(); }}
            className="rounded-lg p-1 hover:bg-white/10 transition-colors"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 px-3 pt-2.5 pb-1">
          {([
            { id: 'click', label: 'Click', Icon: MousePointer2 },
            { id: 'box',   label: 'Box',   Icon: Square },
            { id: 'lasso', label: 'Lasso', Icon: PenLine },
          ] as const).map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => { setMode(id); modeRef.current = id; clearSelection(); }}
              className="flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl transition-all"
              style={{
                background: mode === id ? 'rgba(99,210,255,0.18)' : 'rgba(255,255,255,0.05)',
                border: mode === id ? '1px solid rgba(99,210,255,0.4)' : '1px solid transparent',
                color: mode === id ? 'rgba(99,210,255,0.9)' : 'rgba(255,255,255,0.45)',
                fontSize: 10, fontWeight: 600,
              }}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Status */}
        <div className="px-3 py-2">
          <div
            className="rounded-lg px-2.5 py-2 text-center"
            style={{ background: 'rgba(255,255,255,0.05)', minHeight: 36 }}
          >
            <span style={{ fontSize: 11, color: drawState === 'done' ? confidenceColor : 'rgba(255,255,255,0.5)' }}>
              {statusText}
            </span>
          </div>
        </div>

        {/* Actions */}
        {drawState === 'done' && (
          <div className="flex gap-2 px-3 pb-3">
            <button
              onClick={clearSelection}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl transition-colors"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 500 }}
            >
              <X size={12} /> Clear
            </button>
            <button
              onClick={() => { if (selection) onDone?.(selection); onClose(); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl transition-colors"
              style={{ background: 'rgba(99,210,255,0.18)', color: 'rgba(99,210,255,0.9)', fontSize: 12, fontWeight: 600, border: '1px solid rgba(99,210,255,0.35)' }}
            >
              <Check size={12} /> Done
            </button>
          </div>
        )}

        {drawState === 'idle' && (
          <div className="px-3 pb-3">
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textAlign: 'center', lineHeight: 1.4 }}>
              Selection context is sent with your next query
            </p>
          </div>
        )}
      </div>
    </>
  );
}
